import type { QueryClient, QueryKey } from "react-query";
import type { IWelcomeDashboardAccessResponse } from "../../service/api/APIWelcomeDashboardAccess";

export const WELCOME_DASHBOARD_ACCESS_KEY = "welcome-dashboard-access";
export const WELCOME_AI_MUTATION_KEY = "welcome-order-analysis-ai";
export const dashboardAccessKey = (actorId: string) => [WELCOME_DASHBOARD_ACCESS_KEY, actorId] as const;
export const deniedDashboardAccess = (): IWelcomeDashboardAccessResponse => ({
  status: true, data: { permissionCode: "00-01", canViewDashboard: false },
});

export const hasFreshDashboardAccess = (state: {
  actorId: string;
  isFetchedAfterMount: boolean;
  isError: boolean;
  data?: IWelcomeDashboardAccessResponse;
}): boolean => Boolean(
  state.actorId && state.isFetchedAfterMount && !state.isError &&
  state.data?.status === true && state.data.data.permissionCode === "00-01" &&
  state.data.data.canViewDashboard === true
);

const sensitiveKeys = new Set([
  "welcome-daily-sales",
  "welcome-branch-order-behavior", "welcome-branch-order-evidence",
  "welcome-order-analytics", "welcome-order-franchises", "welcome-supporting-summary",
  "WELCOME_GET_SYSTEM_HEALTH", "WELCOME_GET_TODAY_SUMMARY", "GET_DASHBOARD_SUMMARY",
  "GET_AI_INSIGHT", "FUEL_PRICE_BANGCHAK",
]);

export const clearWelcomeDashboardData = async (client: QueryClient): Promise<void> => {
  const filters = { predicate: (query: { queryKey: QueryKey }) => sensitiveKeys.has(
    typeof query.queryKey === "string" ? query.queryKey : String(query.queryKey[0])
  ) };
  await client.cancelQueries(filters);
  client.removeQueries(filters);
  client.getMutationCache().getAll().forEach((mutation) => {
    if (mutation.options.mutationKey?.[0] === WELCOME_AI_MUTATION_KEY) client.getMutationCache().remove(mutation);
  });
};

/** Permission and membership edits must invalidate server capabilities, including this actor's. */
export const invalidateWelcomeDashboardAccess = async (client: QueryClient): Promise<void> => {
  const cancellation = client.cancelQueries([WELCOME_DASHBOARD_ACCESS_KEY], undefined, { revert: false });
  client.setQueriesData([WELCOME_DASHBOARD_ACCESS_KEY], deniedDashboardAccess());
  await cancellation;
  await clearWelcomeDashboardData(client);
  await client.invalidateQueries([WELCOME_DASHBOARD_ACCESS_KEY]);
};

export const isDashboardAccessRejection = (error: unknown): boolean => {
  const candidate = error as { response?: { status?: number }; config?: { url?: string } } | null;
  const status = candidate?.response?.status;
  const url = candidate?.config?.url || "";
  return (status === 401 || status === 403) && /(?:^|\/)dashboard\//.test(url) && !/\/dashboard\/access(?:\?|$)/.test(url);
};
